import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface MarkAsUnfinishedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: { currentPage?: number; reason: string }) => void;
  bookTitle: string;
  totalPages?: number;
  currentPage?: number;
  loading?: boolean;
}

export function MarkAsUnfinishedDialog({
  open,
  onOpenChange,
  onConfirm,
  bookTitle,
  totalPages,
  currentPage: initialCurrentPage,
  loading = false
}: MarkAsUnfinishedDialogProps) {
  const [currentPage, setCurrentPage] = useState<string>(initialCurrentPage?.toString() || '');
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    const pageNum = currentPage ? parseInt(currentPage, 10) : undefined;
    onConfirm({
      currentPage: pageNum,
      reason: reason.trim()
    });
  };

  const handleClose = () => {
    if (!loading) {
      setCurrentPage(initialCurrentPage?.toString() || '');
      setReason('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mark as Unfinished</DialogTitle>
          <DialogDescription>
            Track where you stopped reading "{bookTitle}"
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="currentPage">
              What page did you reach? (optional)
            </Label>
            <Input
              id="currentPage"
              type="number"
              min="0"
              max={totalPages}
              value={currentPage}
              onChange={(e) => setCurrentPage(e.target.value)}
              placeholder="e.g., 150"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">
              Why didn't you finish? (optional)
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Lost interest, too slow, not what I expected..."
              rows={4}
              disabled={loading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MarkAsUnfinishedDialog;
